/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import ApexCharts, { ApexOptions } from 'apexcharts';
import { useHistory } from 'react-router-dom';
import {
  getOffersOperation,
  OffersOperationResponse,
} from '../../../service/dashboard/Dashboard';

interface OfferItem {
  offer_name: string;
  count: number;
  percentage: number;
}

type Props = {
  init_date?: string;
  end_date?: string;
};

const OfferDashboard: React.FC<Props> = ({ init_date, end_date }) => {
  const history = useHistory();
  const fechaActual = new Date();
  const fecha_inicio_init = new Date(
    fechaActual.getFullYear(),
    fechaActual.getMonth(),
    1,
  );
  const fecha_fin_init = new Date();

  const [initFilterValues, setInitFilterValues] = useState({
    init_date: init_date || fecha_inicio_init.toISOString().split('T')[0],
    end_date: end_date || fecha_fin_init.toISOString().split('T')[0],
  });

  // Actualizar filtros cuando cambien las props
  useEffect(() => {
    if (init_date !== undefined && end_date !== undefined) {
      setInitFilterValues({
        init_date,
        end_date,
      });
    }
  }, [init_date, end_date]);
  const [selectedOption, setSelectedOption] = useState<number>(1);
  const [data, setData] = useState<OffersOperationResponse>({
    activations: [],
    portabilities: [],
    recharges: [],
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [dataRoute, setDataRoute] = useState<string>('/operacion/activaciones');
  const handleTabChange = (option: number) => {
    setSelectedOption(option);
    switch (option) {
      case 1:
        setDataRoute('/operacion/activaciones');
        break;
      case 2:
        setDataRoute('/operacion/portabilidades');
        break;
      case 3:
        setDataRoute('/operacion/recargas');
        break;
    }
  };

  // Función para generar colores degradados del más fuerte al más claro
  const generateGradientColors = (count: number): string[] => {
    const startColor = { r: 136, g: 61, b: 207 }; // #883DCF - púrpura fuerte
    const endColor = { r: 245, g: 235, b: 255 }; // #F5EBFF - púrpura muy claro

    if (count <= 1) return ['#883DCF'];

    const colors: string[] = [];
    for (let i = 0; i < count; i++) {
      const ratio = i / (count - 1);
      const r = Math.round(startColor.r + (endColor.r - startColor.r) * ratio);
      const g = Math.round(startColor.g + (endColor.g - startColor.g) * ratio);
      const b = Math.round(startColor.b + (endColor.b - startColor.b) * ratio);
      colors.push(`rgb(${r}, ${g}, ${b})`);
    }
    return colors;
  };

  // Obtener datos según la opción seleccionada
  const currentOffers = useMemo((): OfferItem[] => {
    if (selectedOption === 1) return data.activations || [];
    if (selectedOption === 2) return data.portabilities || [];
    if (selectedOption === 3) return data.recharges || [];
    return [];
  }, [data, selectedOption]);

  // Generar colores basados en la cantidad de ofertas
  const gradientColors = useMemo(() => {
    return generateGradientColors(currentOffers.length);
  }, [currentOffers.length]);

  // Mapear a la estructura para mostrar
  const offersArray = useMemo(() => {
    return currentOffers.map((item, index) => ({
      name: item.offer_name,
      cantidad: item.count,
      porcentaje: item.percentage.toFixed(1),
      color: gradientColors[index],
    }));
  }, [currentOffers, gradientColors]);

  const chartRef = useRef<HTMLDivElement | null>(null);

  const hasData = offersArray.length > 0;

  const chartSeries = useMemo(() => {
    return hasData
      ? offersArray.map(({ porcentaje }) => parseFloat(porcentaje))
      : [100];
  }, [offersArray, hasData]);

  const chartLabels = useMemo(() => {
    return hasData ? offersArray.map(({ name }) => name) : ['Sin datos'];
  }, [offersArray, hasData]);

  const refreshChart = () => {
    if (!chartRef.current) return;
    const chart = new ApexCharts(
      chartRef.current,
      chartOptions(chartSeries, chartLabels, hasData, gradientColors),
    );

    if (chart) {
      chart.render();
    }
    return chart;
  };

  // Cargar datos del servicio
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getOffersOperation({
        init_date: initFilterValues.init_date,
        end_date: initFilterValues.end_date,
      });
      setData(
        response || { activations: [], portabilities: [], recharges: [] },
      );
    } catch (error) {
      console.error('Error al cargar ofertas:', error);
      setData({ activations: [], portabilities: [], recharges: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [initFilterValues]);

  useEffect(() => {
    if (!chartRef.current || chartSeries.length === 0) return;

    chartRef.current.innerHTML = '';

    const chart = new ApexCharts(
      chartRef.current,
      chartOptions(chartSeries, chartLabels, hasData, gradientColors),
    );

    chart.render();

    return () => {
      chart.destroy();
    };
  }, [chartSeries, chartLabels, gradientColors, hasData]);

  return (
    <div className="card card-flush h-100">
      <div className="card-header pt-5 flex-column align-items-start">
        <div className="card-title d-flex flex-column mb-3">
          <span className="fs-2 fw-bolder text-gray-900">Uso de ofertas</span>
          <span className="text-gray-500 pt-1 fw-semibold fs-6">
            Ofertas usadas por operación
          </span>
        </div>
        <ul className="nav nav-tabs nav-line-tabs fs-8 border-0 w-100 justify-content-center flex-wrap">
          <li className="nav-item">
            <a
              className={`nav-link btn btn-sm btn-color-muted btn-active btn-active-light-primary fw-bold px-1 ${
                selectedOption === 1 ? 'active' : ''
              }`}
              onClick={() => handleTabChange(1)}
              style={{ cursor: 'pointer' }}
            >
              Activación
            </a>
          </li>
          <li className="nav-item">
            <a
              className={`nav-link btn btn-sm btn-color-muted btn-active btn-active-light-primary fw-bold px-1 ${
                selectedOption === 2 ? 'active' : ''
              }`}
              onClick={() => handleTabChange(2)}
              style={{ cursor: 'pointer' }}
            >
              Portabilidad
            </a>
          </li>
          <li className="nav-item">
            <a
              className={`nav-link btn btn-sm btn-color-muted btn-active btn-active-light-primary fw-bold px-1 ${
                selectedOption === 3 ? 'active' : ''
              }`}
              onClick={() => handleTabChange(3)}
              style={{ cursor: 'pointer' }}
            >
              Recarga
            </a>
          </li>
        </ul>
      </div>

      <div className="card-body pt-2 pb-4 d-flex flex-column align-items-center">
        <div className="d-flex justify-content-center align-items-center w-100 mb-4">
          <div
            ref={chartRef}
            style={{
              width: '150px',
              height: '150px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          ></div>
        </div>
        <div
          className="d-flex flex-column w-100 mt-5"
          style={{
            maxHeight: '100px',
            overflowY: 'auto',
          }}
        >
          {hasData ? (
            offersArray.map(({ name, cantidad, porcentaje, color }) => (
              <div
                key={name}
                className="d-flex fw-semibold align-items-center justify-content-between pb-2 fs-7"
                style={{
                  // borderBottom: '1px dashed #ccc',
                  paddingBottom: '5px',
                }}
              >
                <div
                  className="d-flex align-items-center fw-bolder"
                  style={{
                    width: '120px',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                  title={name}
                >
                  <div
                    style={{
                      width: '8px',
                      height: '8px',
                      backgroundColor: color,
                      borderRadius: '50%',
                      marginRight: '2px',
                      flexShrink: 0,
                    }}
                  ></div>
                  <span style={{ marginLeft: 4 }}>{name}</span>
                </div>
                <div className="fw-bolder me-4">{cantidad}</div>
                <div className="fw-bolder text-end">{porcentaje} %</div>
              </div>
            ))
          ) : (
            <div className="text-center text-muted py-4">Sin información</div>
          )}
        </div>

        <div className="mt-5">
          <button
            className="btn btn-sm btn-light-primary fs-6"
            onClick={() => {
              history.push(dataRoute);
            }}
          >
            Ver detalles
            <i className="bi bi-arrow-right ms-2" />
          </button>
        </div>
      </div>
    </div>
  );
};

const isLightColor = (color: string): boolean => {
  let r: number, g: number, b: number;

  if (color.startsWith('rgb')) {
    const match = color.match(/\d+/g);
    if (match) {
      [r, g, b] = match.map(Number);
    } else {
      return false;
    }
  } else if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    r = parseInt(hex.substr(0, 2), 16);
    g = parseInt(hex.substr(2, 2), 16);
    b = parseInt(hex.substr(4, 2), 16);
  } else {
    return false;
  }

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.7;
};

const chartOptions = (
  series: number[],
  labels: string[],
  hasData: boolean,
  colors?: string[],
): ApexOptions => {
  const customColors =
    hasData && colors && colors.length > 0
      ? colors
      : hasData
      ? ['#883DCF']
      : ['#E0E0E0'];

  return {
    series,
    labels,
    chart: {
      width: '270px',
      type: 'donut',
    },
    colors: customColors,
    legend: {
      show: false,
    },
    plotOptions: {
      pie: {
        customScale: 0.85,
        donut: {
          size: '68%',
          labels: {
            show: true,
            name: {
              show: false,
            },
            value: {
              show: true,
              fontSize: '22px',
              fontWeight: 'bold',
              color: '#000',
              formatter: function (val) {
                return hasData ? val + '%' : '0%';
              },
            },
            total: {
              show: true,
              label: 'Total',
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#333',
              formatter: function (w) {
                return hasData ? '100%' : '0%';
              },
            },
          },
        },
      },
    },
    stroke: {
      show: true,
      width: 5,
      colors: ['#fff'],
      lineCap: 'round',
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      enabled: hasData,
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const value = series[seriesIndex];
        const label = w.globals.labels[seriesIndex];
        const color = customColors[seriesIndex] || '#883DCF';
        const textColor = isLightColor(color) ? '#000000' : '#ffffff';

        return `<div style="
          background-color: ${color};
          color: ${textColor};
          padding: 8px 12px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        ">
          <span>${label}: ${value.toFixed(1)}%</span>
        </div>`;
      },
    },
  };
};

export { OfferDashboard };
